import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader } from 'lucide-react';
import { gymDarkHeaderBar } from '../gymPageTheme';
import MemberCheckboxList from './MemberCheckboxList';
import CourseSessionSelector from './CourseSessionSelector';
import EnrollmentPreview from './EnrollmentPreview';
import BulkEnrollmentCapacityModal, {
    analyzeBulkEnrollmentCapacity,
} from './BulkEnrollmentCapacityModal';
import BulkEnrollmentResultModal from './BulkEnrollmentResultModal';
import { idKey } from '../../../utils/courseSessionPattern';

export default function BulkEnrollmentPage() {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const navigate = useNavigate();

    const [selectedMembers, setSelectedMembers] = useState([]);
    const [selectedSessions, setSelectedSessions] = useState([]);
    const [membersList, setMembersList] = useState([]);
    const [sessionsById, setSessionsById] = useState({});
    const [sessionsRefreshKey, setSessionsRefreshKey] = useState(0);
    const [sendNotification, setSendNotification] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [resultSummary, setResultSummary] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [capacityConflicts, setCapacityConflicts] = useState([]);
    const [capacityOkCount, setCapacityOkCount] = useState(0);
    const [showCapacityModal, setShowCapacityModal] = useState(false);
    const [alreadyBySession, setAlreadyBySession] = useState({});

    const membersById = useMemo(() => {
        const map = {};
        membersList.forEach((member) => {
            map[idKey(member.id)] = member;
        });
        return map;
    }, [membersList]);

    const runEnrollmentPlan = async ({ okSessions = [], conflictPlan = [] }) => {
        setIsProcessing(true);
        try {
            const api = getAuthenticatedApi();
            let totalCreated = 0;
            let duplicatesSkipped = 0;
            const errors = [];
            let waitlistAdded = 0;

            okSessions.forEach((item) => {
                duplicatesSkipped += (item.duplicateUserIds || []).length;
            });
            conflictPlan.forEach((row) => {
                duplicatesSkipped += (row.duplicateUserIds || []).length;
            });

            // 1) Sessioni senza conflitti: iscrizione solo dei candidati nuovi
            for (const item of okSessions) {
                const enrollIds = item.enrollUserIds || [];
                if (!enrollIds.length) continue;
                const response = await api.post('/gym_courses.php', {
                    action: 'bulk_enroll_multiple_sessions',
                    user_ids: enrollIds,
                    session_ids: [item.session.id],
                    send_notification: sendNotification,
                });
                if (response.data.success) {
                    totalCreated += response.data.total_enrollments_created || 0;
                    duplicatesSkipped += response.data.duplicates_skipped || 0;
                    (response.data.errors || []).forEach((err) => errors.push(err));
                }
            }

            // 2) Sessioni in conflitto: prima iscrivi chi ha posto (riempie), poi lista d'attesa
            for (const row of conflictPlan) {
                if (row.enrollUserIds?.length) {
                    const response = await api.post('/gym_courses.php', {
                        action: 'bulk_enroll_multiple_sessions',
                        user_ids: row.enrollUserIds,
                        session_ids: [row.sessionId],
                        send_notification: sendNotification,
                    });
                    if (response.data.success) {
                        totalCreated += response.data.total_enrollments_created || 0;
                        duplicatesSkipped += response.data.duplicates_skipped || 0;
                        (response.data.errors || []).forEach((err) => errors.push(err));
                    }
                }
                if (row.waitlistUserIds?.length) {
                    const waitResponse = await api.post('/gym_courses.php?action=add_to_waitlist', {
                        user_ids: row.waitlistUserIds,
                        session_id: row.sessionId,
                    });
                    waitlistAdded += waitResponse.data.added_count || 0;
                    (waitResponse.data.errors || []).forEach((err) => errors.push(err));
                }
            }

            setResultSummary({
                created: totalCreated,
                waitlist: waitlistAdded,
                duplicates: duplicatesSkipped,
                errors,
            });
            setShowResult(true);
            setSelectedMembers([]);
            setSelectedSessions([]);
            setSendNotification(true);
            setSessionsRefreshKey((key) => key + 1);
        } catch (err) {
            console.error('Errore durante iscrizione corsi:', err);
            toast.error(
                `${t('gym.bulkEnrollment.errors.enrollmentError')}: ` +
                    (err.response?.data?.error || err.message || 'Errore sconosciuto')
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const refreshSessionsSnapshot = async () => {
        try {
            const api = getAuthenticatedApi();
            const months = [];
            const today = new Date();
            for (let i = 0; i < 6; i += 1) {
                const cursor = new Date(today.getFullYear(), today.getMonth() + i, 1);
                months.push(
                    `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
                );
            }
            const responses = await Promise.all(
                months.map((month) => api.get(`/gym_courses.php?action=list_sessions&month=${month}`))
            );
            const byId = {};
            responses
                .flatMap((response) => response.data.sessions || [])
                .forEach((session) => {
                    byId[idKey(session.id)] = session;
                });
            setSessionsById(byId);
            setSessionsRefreshKey((key) => key + 1);
            return byId;
        } catch (err) {
            console.error('Errore refresh sessioni:', err);
            return sessionsById;
        }
    };

    const fetchAlreadyBySessions = async (sessionIds) => {
        const api = getAuthenticatedApi();
        const uniqueIds = [...new Set(sessionIds.map((id) => idKey(id)))];
        const responses = await Promise.all(
            uniqueIds.map((id) =>
                api.get(`/gym_courses.php?action=get_session&id=${id}`).catch((err) => {
                    console.error('Errore dettaglio sessione:', id, err);
                    return null;
                })
            )
        );
        const map = {};
        responses.forEach((response, index) => {
            const sid = uniqueIds[index];
            const enrolled = [];
            const waitlisted = [];
            const session = response?.data?.session;
            (session?.enrollments || []).forEach((enrollment) => {
                const status = String(enrollment.status || '');
                if (['enrolled', 'attended', 'absent'].includes(status)) {
                    enrolled.push(Number(enrollment.user_id));
                }
            });
            (session?.waitlist || []).forEach((row) => {
                const status = String(row.status || '');
                if (['waiting', 'offered'].includes(status)) {
                    waitlisted.push(Number(row.user_id));
                }
            });
            map[sid] = { enrolled, waitlisted };
        });
        setAlreadyBySession(map);
        return map;
    };

    const handleConfirm = async () => {
        if (selectedMembers.length === 0 || selectedSessions.length === 0) {
            toast.error(t('gym.bulkEnrollment.errors.selectAtLeastOne'));
            return;
        }

        setIsProcessing(true);
        let latestSessions;
        let alreadyMap;
        try {
            latestSessions = await refreshSessionsSnapshot();
            alreadyMap = await fetchAlreadyBySessions(selectedSessions);
        } finally {
            setIsProcessing(false);
        }

        const { okSessions, conflictSessions } = analyzeBulkEnrollmentCapacity(
            selectedSessions,
            selectedMembers,
            latestSessions,
            alreadyMap
        );

        if (conflictSessions.length > 0) {
            setCapacityConflicts(conflictSessions);
            setCapacityOkCount(okSessions.length);
            setShowCapacityModal(true);
            return;
        }

        const newEnrollments = okSessions.reduce(
            (sum, item) => sum + (item.enrollUserIds?.length || 0),
            0
        );
        const knownDuplicates = okSessions.reduce(
            (sum, item) => sum + (item.duplicateUserIds?.length || 0),
            0
        );

        if (newEnrollments === 0) {
            await runEnrollmentPlan({ okSessions, conflictPlan: [] });
            return;
        }

        if (
            !window.confirm(
                t('gym.bulkEnrollment.confirmation.title', {
                    count: newEnrollments,
                    users: selectedMembers.length,
                    sessions: selectedSessions.length,
                }) +
                    (knownDuplicates > 0
                        ? `\n\n${t('gym.bulkEnrollment.confirmation.duplicatesNote', {
                              count: knownDuplicates,
                          })}`
                        : '')
            )
        ) {
            return;
        }

        await runEnrollmentPlan({ okSessions, conflictPlan: [] });
    };

    const handleCapacityConfirm = async (conflictPlan) => {
        setShowCapacityModal(false);
        const { okSessions } = analyzeBulkEnrollmentCapacity(
            selectedSessions,
            selectedMembers,
            sessionsById,
            alreadyBySession
        );
        await runEnrollmentPlan({ okSessions, conflictPlan });
    };

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
            <div className={`shrink-0 border-b border-gray-200 px-4 py-4 dark:border-gray-800 sm:px-6 ${isDark ? gymDarkHeaderBar : 'bg-white'}`}>
                <button
                    type="button"
                    onClick={() => navigate('/gym/members')}
                    className="mb-2 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                    <ArrowLeft size={18} />
                    <span>{t('gym.bulkEnrollment.backToMembers')}</span>
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
                    {t('gym.bulkEnrollment.title')}
                </h1>
                <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
                    {t('gym.bulkEnrollment.description')}
                </p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col xl:flex-row">
                <aside className="h-[min(440px,48vh)] shrink-0 overflow-hidden border-b border-gray-200 dark:border-gray-800 xl:h-auto xl:w-[400px] xl:border-b-0 xl:border-r">
                    <MemberCheckboxList
                        selectedMembers={selectedMembers}
                        onSelectionChange={setSelectedMembers}
                        onMembersChange={setMembersList}
                    />
                </aside>

                <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-gray-50 dark:bg-gray-950">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <CourseSessionSelector
                            selectedSessions={selectedSessions}
                            onSelectionChange={setSelectedSessions}
                            refreshKey={sessionsRefreshKey}
                            onSessionsByIdChange={setSessionsById}
                        />
                    </div>
                    <EnrollmentPreview
                        selectedMembers={selectedMembers}
                        selectedSessions={selectedSessions}
                        sendNotification={sendNotification}
                        onToggleNotification={setSendNotification}
                        onConfirm={handleConfirm}
                    />
                </section>
            </div>

            <BulkEnrollmentCapacityModal
                isOpen={showCapacityModal}
                conflicts={capacityConflicts}
                okCount={capacityOkCount}
                membersById={membersById}
                onCancel={() => setShowCapacityModal(false)}
                onConfirm={handleCapacityConfirm}
            />

            <BulkEnrollmentResultModal
                isOpen={showResult}
                result={resultSummary}
                onClose={() => {
                    setShowResult(false);
                    setResultSummary(null);
                }}
            />

            {isProcessing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-white/10 dark:bg-gray-900">
                        <Loader className="mx-auto mb-4 animate-spin text-blue-600" size={48} />
                        <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
                            {t('gym.bulkEnrollment.processing.title')}
                        </h3>
                        <p className="text-gray-600 dark:text-white/75">
                            {t('gym.bulkEnrollment.processing.message')}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
